<template>
    <div class="notification-bell" ref="notificationRef">
        <Bell :size="24" class="has-text-light" @click="toggleNotifications" />
        <span v-if="unreadCount > 0" class="badge">{{ unreadCount }}</span>

        <div v-if="showDropdown" class="notif-panel" ref="panelRef" @scroll="onPanelScroll">
            <div class="notif-header">
                <span class="notif-title">Notificaciones</span>
                <span v-if="unreadCount > 0" class="unread-chip">{{ unreadCount }} sin leer</span>
            </div>
            <ul v-if="notifications.length > 0">
                <li v-for="notification in notifications" :key="notification.id"
                    :ref="(el) => observeNotification(el, notification)" class="notification-item"
                    :class="{ unread: !notification.status }">
                    <div class="notification-content">
                        <span v-if="!notification.status" class="unread-dot"></span>
                        <div class="notification-body">
                            <strong class="notification-title">{{ notification.title }}</strong>
                            <p class="notification-message">{{ notification.message }}</p>
                            <p class="notification-date">{{ getDate(notification) }}</p>
                        </div>
                    </div>
                </li>
            </ul>
            <ul v-else-if="!loading">
                <li class="notification-item empty-state">
                    <Bell :size="32" class="empty-icon" />
                    <p class="empty-text">No tienes notificaciones</p>
                </li>
            </ul>

            <div class="notif-footer">
                <span v-if="loading" class="notif-spinner"></span>
                <span v-else-if="!hasMore && notifications.length > 0" class="no-more-text">
                    Ya no hay más notificaciones
                </span>
            </div>
        </div>
    </div>
</template>

<script setup>
    import { ref, onMounted, onUnmounted } from 'vue';
    import { useSocket } from '@/composables/useSocket';
    import apiClient from '@/api/apiClient';
    import { Bell } from 'lucide-vue-next'

    const { notifications, unreadCount, fetchNotifications, loadMore, hasMore, loading } = useSocket();
    const showDropdown = ref(false);
    const notificationRef = ref(null);
    const panelRef = ref(null);

    const toggleNotifications = () => {
        showDropdown.value = !showDropdown.value;
        if (showDropdown.value) {
            fetchNotifications();
        }
    };

    const onPanelScroll = () => {
        const el = panelRef.value;
        if (!el) return;
        if (el.scrollTop + el.clientHeight >= el.scrollHeight - 60) {
            loadMore();
        }
    };

    const handleClickOutside = (event) => {
        if (notificationRef.value && !notificationRef.value.contains(event.target)) {
            showDropdown.value = false;
        }
    };

    onMounted(() => {
        document.addEventListener('click', handleClickOutside);
    });

    onUnmounted(() => {
        document.removeEventListener('click', handleClickOutside);
    });

    const getDate = (notification) => {
        const date    = new Date(notification.createdAt);
        const fecha   = `${date.getDate()}/${date.getMonth() + 1}/${date.getFullYear()}`;
        const hours   = date.getHours().toString().padStart(2, '0');
        const minutes = date.getMinutes().toString().padStart(2, '0');

        return `${fecha} - ${hours}:${minutes}`;
    };

    const observeNotification = (el, notification) => {
        if (!el || notification.status) return;

        const observer = new IntersectionObserver(async ([entry]) => {
            if (entry.isIntersecting) {
                await markAsRead(notification);
                observer.unobserve(entry.target);
            }
        }, { threshold: 0.9 });
        observer.observe(el);
    };

    const markAsRead = async (notification) => {
        try {
            await apiClient.put(`/notifications/read/${notification.id}`);
            notification.status = true;
        } catch (error) {
            console.error('❌ Error al marcar como leído:', error);
        }
    };
</script>
  
<style scoped>
    .notification-bell {
        position: relative;
        cursor: pointer;
    }

    .badge {
        position: absolute;
        top: -6px;
        left: 12px;
        background: #e53e3e;
        color: white;
        font-size: 10px;
        font-weight: 700;
        border-radius: 50%;
        padding: 2px 5px;
        min-width: 18px;
        text-align: center;
        line-height: 1.2;
        box-shadow: 0 1px 3px rgba(0,0,0,0.25);
    }

    .notif-panel {
        position: absolute;
        top: 36px;
        right: 0;
        background: white;
        border: 1px solid #e2e8f0;
        border-radius: 0.75rem;
        width: 400px;
        max-height: 520px;
        overflow-y: auto;
        box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
        z-index: 9999;
        display: flex;
        flex-direction: column;
    }

    .notif-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 14px 16px;
        border-bottom: 1px solid #e2e8f0;
        position: sticky;
        top: 0;
        background: white;
        z-index: 1;
        flex-shrink: 0;
    }

    .notif-title {
        font-size: 1rem;
        font-weight: 700;
        color: #1a202c;
    }

    .unread-chip {
        font-size: 0.72rem;
        font-weight: 600;
        background: #ebf4ff;
        color: var(--color-primary, #3182ce);
        border-radius: 999px;
        padding: 2px 10px;
    }

    ul {
        list-style: none;
        padding: 0;
        margin: 0;
    }

    .notification-item {
        padding: 14px 16px;
        border-bottom: 1px solid #f0f0f0;
        transition: background 0.2s ease;
    }

    .notification-item:last-child {
        border-bottom: none;
    }

    .notification-item:hover {
        background-color: #f7fafc;
    }

    .notification-content {
        display: flex;
        align-items: flex-start;
        gap: 10px;
    }

    .unread-dot {
        flex-shrink: 0;
        width: 9px;
        height: 9px;
        border-radius: 50%;
        background: var(--color-primary, #3182ce);
        margin-top: 5px;
    }

    .notification-body {
        flex: 1;
        min-width: 0;
    }

    .notification-title {
        display: block;
        font-size: 0.95rem;
        font-weight: 700;
        color: #2d3748;
        margin-bottom: 4px;
    }

    .notification-message {
        font-size: 0.85rem;
        color: #4a5568;
        margin: 0 0 6px;
        line-height: 1.4;
        white-space: pre-line;
    }

    .notification-date {
        font-size: 0.75rem;
        color: #a0aec0;
        margin: 0;
    }

    .unread {
        background-color: #f0f6ff;
    }

    .empty-state {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        padding: 48px 16px;
        gap: 12px;
    }

    .empty-icon {
        color: #cbd5e0;
    }

    .empty-text {
        font-size: 0.95rem;
        color: #a0aec0;
        margin: 0;
    }

    .notif-footer {
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 12px 16px;
        min-height: 44px;
    }

    .no-more-text {
        font-size: 0.78rem;
        color: #b0bec5;
    }

    .notif-spinner {
        display: inline-block;
        width: 18px;
        height: 18px;
        border: 2px solid #e2e8f0;
        border-top-color: var(--color-primary, #3182ce);
        border-radius: 50%;
        animation: spin 0.7s linear infinite;
    }

    @keyframes spin {
        to { transform: rotate(360deg); }
    }

    /* Móvil */
    @media (max-width: 640px) {
        .notif-panel {
            position: fixed;
            top: auto;
            bottom: 0;
            left: 0;
            right: 0;
            width: 100%;
            max-height: 93vh;
            border-radius: 1rem 1rem 0 0;
            border: none;
            box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.15);
        }

        .notif-header {
            border-radius: 1rem 1rem 0 0;
            padding: 16px 20px;
        }

        .notification-item {
            padding: 16px 20px;
        }
    }
</style>